import { FormElement, TranslateableText } from '@woorcs/form'
import { pipe } from 'fp-ts/function'
import { uuid } from '@woorcs/types/UUID'

const copyTextElement = (
  element: FormElement.TextElement
): FormElement.TextElement => {
  return FormElement.textElement({
    ...element,
    text: TranslateableText.copy(element.text)
  })
}

const withoutId = <T extends FormElement.FormElementType>({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  id,
  ...element
}: T): Omit<T, 'id'> => {
  return element
}

export const copyElement = (
  element: FormElement.FormElementType
): FormElement.FormElementType => {
  return pipe(
    element,
    FormElement.FormElementType.match({
      Text: copyTextElement,
      Alert: (element) =>
        FormElement.alertElement({
          ...withoutId(element),
          children: element.children.map(copyTextElement)
        }),
      Fieldset: (element) =>
        FormElement.fieldsetElement({
          ...withoutId(element),
          children: element.children.map(
            copyElement
          ) as FormElement.InputElementType[]
        }),
      TextInput: (element) =>
        FormElement.textInputElement({
          ...withoutId(element),
          key: uuid(),
          label: TranslateableText.copy(element.label),
          placeholder: TranslateableText.copy(element.placeholder),
          informativeText: TranslateableText.copy(element.informativeText)
        }),
      NumberInput: (element) =>
        FormElement.numberInputElement({
          ...withoutId(element),
          key: uuid(),
          label: TranslateableText.copy(element.label),
          placeholder: TranslateableText.copy(element.placeholder),
          informativeText: TranslateableText.copy(element.informativeText)
        }),
      EmailInput: (element) =>
        FormElement.emailInputElement({
          ...withoutId(element),
          key: uuid(),
          label: TranslateableText.copy(element.label),
          placeholder: TranslateableText.copy(element.label),
          informativeText: TranslateableText.copy(element.informativeText)
        }),
      LocationInput: (element) =>
        FormElement.locationInputElement({
          ...withoutId(element),
          key: uuid(),
          label: TranslateableText.copy(element.label),
          informativeText: TranslateableText.copy(element.informativeText)
        }),
      SignatureInput: (element) =>
        FormElement.signatureInputElement({
          ...withoutId(element),
          key: uuid(),
          label: TranslateableText.copy(element.label),
          informativeText: TranslateableText.copy(element.informativeText)
        }),
      ImageInput: (element) =>
        FormElement.imageInputElement({
          ...withoutId(element),
          key: uuid(),
          label: TranslateableText.copy(element.label),
          informativeText: TranslateableText.copy(element.informativeText)
        }),
      DateInput: (element) =>
        FormElement.dateInputElement({
          ...withoutId(element),
          key: uuid(),
          label: TranslateableText.copy(element.label),
          informativeText: TranslateableText.copy(element.informativeText)
        }),
      DateRangeInput: (element) =>
        FormElement.dateRangeInputElement({
          ...withoutId(element),
          key: uuid(),
          label: TranslateableText.copy(element.label),
          informativeText: TranslateableText.copy(element.informativeText)
        }),
      TimeInput: (element) =>
        FormElement.timeInputElement({
          ...withoutId(element),
          key: uuid(),
          label: TranslateableText.copy(element.label),
          placeholder: TranslateableText.copy(element.label),
          informativeText: TranslateableText.copy(element.informativeText)
        }),
      SelectInput: (element) =>
        FormElement.selectInputElement({
          ...withoutId(element),
          key: uuid(),
          label: TranslateableText.copy(element.label),
          informativeText: TranslateableText.copy(element.informativeText)
        }),
      MultiSelectInput: (element) =>
        FormElement.multiSelectInputElement({
          ...withoutId(element),
          key: uuid(),
          label: TranslateableText.copy(element.label),
          informativeText: TranslateableText.copy(element.informativeText)
        }),
      GroupInput: (element) =>
        FormElement.groupInputElement({
          ...withoutId(element),
          key: uuid(),
          label: TranslateableText.copy(element.label),
          informativeText: TranslateableText.copy(element.informativeText)
        })
    })
  )
}
